import React from 'react';
import 'assets/scss/modules/progress.scss';
import CompletedIcon from 'assets/img/progress/active.svg';
import InProgressIcon from 'assets/img/progress/inactive.svg';
import classNames from 'classnames';
import { Props } from './progress.type';

type StepStatus = 'completed' | 'current' | 'upcoming';

const TaskIcon = ({ done }: { done: boolean }) => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    {done ? (
      <path
        d="M3 8.4L6.2 11.6L13 4.4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    ) : (
      <circle cx="8" cy="8" r="5.4" stroke="currentColor" strokeWidth="1.6" />
    )}
  </svg>
);

function Progress({ steps }: Props) {
  const currentIndex = (() => {
    const idx = steps.findIndex(s => s.percentage < 100);
    return idx === -1 ? steps.length - 1 : idx;
  })();

  const getStatus = (index: number): StepStatus => {
    if (index < currentIndex) return 'completed';
    if (index === currentIndex) {
      return steps[index].percentage >= 100 ? 'completed' : 'current';
    }
    return 'upcoming';
  };

  const spotlightStep = steps[currentIndex];
  const spotlightStatus = getStatus(currentIndex);
  const nextTask = spotlightStep?.child.find(({ done }) => !done);

  return (
    <div className="journey_progress">
      <div className="journey_progress__track">
        {steps.map((s, index) => {
          const status = getStatus(index);
          const doneCount = s.child.filter(({ done }) => done).length;

          return (
            <React.Fragment key={s.stepIndex}>
              <div className={classNames('journey_progress__node', status)}>
                <div className="journey_progress__node__marker">
                  {status === 'current' ? (
                    <span className="journey_progress__node__marker__index">
                      {s.stepIndex}
                    </span>
                  ) : (
                    <img
                      src={
                        status === 'completed' ? CompletedIcon : InProgressIcon
                      }
                      width={32}
                      height={32}
                      alt=""
                    />
                  )}
                </div>
                <div className="journey_progress__node__label">
                  <span className="journey_progress__node__label__title">
                    {s.step}
                  </span>
                  <span className="journey_progress__node__label__meta">
                    {status === 'completed' && 'Completed'}
                    {status === 'current' &&
                      `${doneCount}/${s.child.length} done`}
                    {status === 'upcoming' && 'Upcoming'}
                  </span>
                </div>
              </div>
              {index < steps.length - 1 && (
                <div className="journey_progress__connector">
                  <span
                    className="journey_progress__connector__fill"
                    style={{
                      width: `${
                        status === 'completed'
                          ? 100
                          : status === 'current'
                          ? s.percentage
                          : 0
                      }%`
                    }}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {spotlightStep && (
        <div className="journey_progress__spotlight">
          <div className="journey_progress__spotlight__info">
            <span className="journey_progress__spotlight__tag">
              {spotlightStatus === 'completed' ? 'All done' : 'Current stage'}
            </span>
            <h3 className="journey_progress__spotlight__title">
              {spotlightStep.step}
            </h3>
            <div className="journey_progress__spotlight__bar">
              <div
                className="journey_progress__spotlight__bar__fill"
                style={{ width: `${spotlightStep.percentage}%` }}
              />
            </div>
          </div>

          <div className="journey_progress__spotlight__tasks">
            {spotlightStep.child.map(({ step: childStep, done }, idx) => {
              const isNext = !done && nextTask?.step === childStep;
              return (
                <div
                  key={idx}
                  className={classNames('journey_progress__task', {
                    done,
                    next: isNext
                  })}
                >
                  <TaskIcon done={done} />
                  <span>{childStep}</span>
                  {isNext && (
                    <span className="journey_progress__task__badge">Next</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default Progress;
